import { memo, useEffect, useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import {
  TrendingUp,
  TrendingDown,
  BarChart2,
  MessageCircle,
  Heart,
  Radio,
  AlertTriangle,
  BarChart,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useTheme } from '../hooks/useTheme'
import { useAnalyticsDashboard } from '../../features/metrics/state/useAnalyticsDashboard'
import type {
  MetricSummary,
  PlatformMetric,
  SentimentBreakdown,
  TimeSeriesPoint,
} from '../../features/metrics/model/contracts'
import { normalizePlatform, SOCIAL_PLATFORMS } from '../../features/posts/model/platforms'
import { Skeleton } from './Skeleton'
import { WordCloud } from './WordCloud'
import { RadarChart } from './RadarChart'
import { NetworkGraph } from './NetworkGraph'

/* ── Shared constants ── */

const CHART_FONT = "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
const card =
  'rounded-2xl border border-border bg-card p-4 shadow-sm shadow-[var(--shadow-brand-highlight)] dark:shadow-none'

/* ── Main Component ── */

interface AnalyticsDashboardProps {
  variant?: 'compact' | 'page'
}

export const AnalyticsDashboard = memo(function AnalyticsDashboard({
  variant = 'compact',
}: AnalyticsDashboardProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { data, isLoading, error } = useAnalyticsDashboard()

  if (isLoading) return variant === 'page' ? <PageSkeleton /> : <CompactSkeleton />

  if (error) {
    return (
      <div
        className={cn(
          card,
          'flex flex-col items-center justify-center gap-3 py-12 text-center',
        )}
      >
        <AlertTriangle size={32} className="text-destructive" />
        <p className="text-sm font-medium text-foreground">{t('common.errorTitle')}</p>
        <p className="max-w-xs text-xs text-muted-foreground">
          {t('analytics.errorDescription', {
            defaultValue: 'Something went wrong while loading analytics data.',
          })}
        </p>
        <button
          type="button"
          onClick={() =>
            queryClient.invalidateQueries({ queryKey: ['analytics-dashboard'] })
          }
          className="mt-1 rounded-lg bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
        >
          {t('common.retry')}
        </button>
      </div>
    )
  }

  if (!data) {
    return (
      <div
        className={cn(
          card,
          'flex flex-col items-center justify-center gap-3 py-12 text-center',
        )}
      >
        <BarChart size={32} className="text-muted-foreground" />
        <p className="text-sm font-medium text-foreground">
          {t('analytics.emptyTitle', { defaultValue: 'No analytics data yet' })}
        </p>
        <p className="max-w-xs text-xs text-muted-foreground">
          {t('analytics.emptyDescription', {
            defaultValue: 'Start monitoring sources to see insights here.',
          })}
        </p>
      </div>
    )
  }

  if (variant === 'page') {
    return (
      <div className="flex min-h-full flex-col gap-4">
        {/* Hero row: Graph (dominant) + KPIs right column */}
        <div className="flex min-h-0 flex-1 flex-col gap-4 md:flex-row">
          {/* Network Graph — central & dominant */}
          <div
            className={cn(
              card,
              'flex min-h-[420px] flex-1 flex-col overflow-hidden md:min-h-0',
            )}
          >
            <NetworkGraph data={data.networkGraph} heightMode="full" />
          </div>

          {/* Right column: KPIs + Sentiment + Trend */}
          <div className="flex w-full shrink-0 flex-col gap-3 md:w-64 lg:w-72 xl:w-80">
            <KpiCard kpi={data.kpis.totalMentions} icon={MessageCircle} />
            <KpiCard kpi={data.kpis.engagementRate} icon={BarChart2} />
            <KpiCard kpi={data.kpis.sentimentScore} icon={Heart} />
            <KpiCard kpi={data.kpis.activeChannels} icon={Radio} />

            <div className={card}>
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {t('analytics.sentiment', { defaultValue: 'Sentiment' })}
              </h3>
              <SentimentGauge sentiment={data.sentiment} />
            </div>
          </div>
        </div>

        {/* Trend chart */}
        <div className={card}>
          <div className="mb-3 flex items-center justify-between">
            <h3 className="m-0 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t('analytics.volume', { defaultValue: 'Mentions' })}
            </h3>
            <span className="text-[10px] text-muted-foreground">7d</span>
          </div>
          <TrendChart data={data.trend} />
        </div>

        {/* Secondary charts */}
        <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:gap-4">
          <div className={card}>
            <h3 className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t('analytics.platformBreakdown', { defaultValue: 'Sources' })}
            </h3>
            <DonutChart platforms={data.platforms} />
          </div>

          <div className={card}>
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t('analytics.topKeywords', { defaultValue: 'Top Keywords' })}
            </h3>
            <WordCloud keywords={data.topKeywords} />
          </div>

          <div className={card}>
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t('analytics.brandHealth', { defaultValue: 'Brand Health' })}
            </h3>
            <RadarChart data={data.competitors} />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4 sm:gap-5">
      {/* KPI Grid */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-4">
        <KpiCard kpi={data.kpis.totalMentions} icon={MessageCircle} />
        <KpiCard kpi={data.kpis.engagementRate} icon={BarChart2} />
        <KpiCard kpi={data.kpis.sentimentScore} icon={Heart} />
        <KpiCard kpi={data.kpis.activeChannels} icon={Radio} />
      </div>

      {/* Trend Chart */}
      <div className={card}>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="m-0 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {t('analytics.volume', { defaultValue: 'Mentions' })}
          </h3>
          <span className="text-[10px] text-muted-foreground">7d</span>
        </div>
        <TrendChart data={data.trend} />
      </div>

      {/* Secondary Charts Grid */}
      <div className="grid gap-3 sm:grid-cols-2 sm:gap-4">
        <div className={card}>
          <h3 className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {t('analytics.platformBreakdown', { defaultValue: 'Sources' })}
          </h3>
          <DonutChart platforms={data.platforms} />
        </div>

        <div className={card}>
          <h3 className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {t('analytics.sentiment', { defaultValue: 'Sentiment' })}
          </h3>
          <SentimentGauge sentiment={data.sentiment} />
        </div>
      </div>

      {/* Word Cloud + Competitor Radar */}
      <div className="grid gap-3 sm:grid-cols-2 sm:gap-4">
        <div className={card}>
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {t('analytics.topKeywords', { defaultValue: 'Top Keywords' })}
          </h3>
          <WordCloud keywords={data.topKeywords} />
        </div>

        <div className={card}>
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {t('analytics.brandHealth', { defaultValue: 'Brand Health' })}
          </h3>
          <RadarChart data={data.competitors} />
        </div>
      </div>

      {/* Network Graph */}
      <div className={card}>
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {t('analytics.networkAnalysis', { defaultValue: 'Network Analysis' })}
        </h3>
        <NetworkGraph data={data.networkGraph} />
      </div>
    </div>
  )
})

/* ── KPI Card ── */

function KpiCard({
  kpi,
  icon: Icon,
}: {
  kpi: MetricSummary
  icon: React.ComponentType<{ size?: number; className?: string }>
}) {
  const isUp = kpi.change.startsWith('+')

  return (
    <div className={card}>
      <div className="mb-2 flex items-center justify-between">
        <Icon size={16} className="text-muted-foreground" />
        <span
          className={cn(
            'flex items-center gap-0.5 text-[11px] font-semibold',
            isUp ? 'text-sentiment-positive' : 'text-sentiment-negative',
          )}
        >
          {isUp ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
          {kpi.change}
        </span>
      </div>
      <div className="text-xl font-bold tabular-nums text-foreground sm:text-2xl">
        {String(kpi.total)}
      </div>
      <div className="mt-0.5 text-[11px] text-muted-foreground">{kpi.label}</div>
    </div>
  )
}

/* ── Trend Chart (pure SVG area) ── */

function TrendChart({ data }: { data: TimeSeriesPoint[] }) {
  const { t } = useTranslation()
  const id = useId()

  if (data.length < 2) return null

  const values = data.map((d) => d.value)
  const max = Math.max(...values)
  const min = Math.min(...values)
  const range = max - min || 1

  const w = 400
  const h = 140
  const px = 4
  const plotW = w - px * 2
  const plotH = h - px * 2

  const points = values.map((v, i) => ({
    x: px + (i / (values.length - 1)) * plotW,
    y: px + plotH - ((v - min) / range) * plotH,
  }))

  const segments = [`M ${points[0].x},${points[0].y}`]
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[Math.max(0, i - 1)]
    const p1 = points[i]
    const p2 = points[i + 1]
    const p3 = points[Math.min(points.length - 1, i + 2)]
    const t = 0.35
    segments.push(
      `C ${p1.x + (p2.x - p0.x) * t},${p1.y + (p2.y - p0.y) * t} ${p2.x - (p3.x - p1.x) * t},${p2.y - (p3.y - p1.y) * t} ${p2.x},${p2.y}`,
    )
  }
  const linePath = segments.join(' ')
  const areaPath = `${linePath} L ${points[points.length - 1].x},${h} L ${points[0].x},${h} Z`

  return (
    <div className="relative">
      <svg
        viewBox={`0 0 ${w} ${h}`}
        preserveAspectRatio="xMidYMid meet"
        className="min-h-[100px] w-full sm:min-h-[140px]"
        role="img"
        aria-label={t('analytics.trendChart.aria', {
          defaultValue:
            'Mentions trend over {{days}} days, ranging from {{min}} to {{max}}',
          days: data.length,
          min,
          max,
        })}
      >
        <title>
          {t('analytics.trendChart.title', { defaultValue: 'Mentions trend chart' })}
        </title>
        <defs>
          <linearGradient id={`${id}-fill`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--chart-fill-start)" />
            <stop offset="100%" stopColor="var(--chart-fill-end)" />
          </linearGradient>
          <linearGradient id={`${id}-stroke`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="var(--chart-line)" />
            <stop offset="50%" stopColor="var(--chart-line-alt)" />
            <stop offset="100%" stopColor="var(--chart-line)" />
          </linearGradient>
        </defs>
        <path d={areaPath} fill={`url(#${id}-fill)`} />
        <path
          d={linePath}
          fill="none"
          stroke={`url(#${id}-stroke)`}
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        {points.map((p, i) => (
          <circle
            key={i}
            cx={p.x}
            cy={p.y}
            r="3"
            fill="var(--chart-line)"
            opacity="0.7"
          />
        ))}
      </svg>
      <div className="mt-1 flex justify-between px-1">
        {data.map((d, i) => (
          <span
            key={d.date}
            className={cn(
              'text-[10px] text-muted-foreground sm:text-[11px]',
              i % 2 !== 0 && 'hidden sm:inline',
            )}
          >
            {d.date}
          </span>
        ))}
      </div>
    </div>
  )
}

/* ── Donut Chart ── */

function DonutChart({ platforms }: { platforms: PlatformMetric[] }) {
  const { t } = useTranslation()
  const { theme } = useTheme()
  const total = platforms.reduce((acc, p) => acc + p.count, 0)
  const radius = 50
  const cx = 60
  const cy = 60
  const strokeWidth = 14

  const readColors = () => {
    const style = getComputedStyle(document.documentElement)
    return {
      [SOCIAL_PLATFORMS.FACEBOOK]:
        style.getPropertyValue('--color-platform-facebook').trim() || '#1877f2',
      [SOCIAL_PLATFORMS.X]:
        style.getPropertyValue('--color-platform-x').trim() || '#1da1f2',
      [SOCIAL_PLATFORMS.THREADS]:
        style.getPropertyValue('--color-platform-threads').trim() || '#a855f7',
      [SOCIAL_PLATFORMS.OTHERS]:
        style.getPropertyValue('--color-platform-others').trim() || '#a1a1aa',
    } as Record<string, string>
  }
  const [COLORS, setCOLORS] = useState(readColors)
  useEffect(() => {
    requestAnimationFrame(() => setCOLORS(readColors()))
  }, [theme])

  const circumference = 2 * Math.PI * radius

  const segments = platforms.map((p) => {
    const pct = total > 0 ? p.count / total : 0
    const dash = circumference * pct
    return {
      platform: p,
      dash,
      gap: circumference - dash,
      normalized: normalizePlatform(p.name),
    }
  })
  const offsets = segments.reduce<number[]>((acc, _, i) => {
    acc.push(i === 0 ? 0 : acc[i - 1] + segments[i - 1].dash)
    return acc
  }, [])

  return (
    <div className="flex items-center gap-4">
      <svg
        width={120}
        height={120}
        viewBox="0 0 120 120"
        className="shrink-0"
        role="img"
        aria-label={t('analytics.donutChart.aria', {
          defaultValue: 'Platform distribution: {{total}} total mentions',
          total,
        })}
      >
        <title>
          {t('analytics.donutChart.title', {
            defaultValue: 'Platform distribution chart',
          })}
        </title>
        {segments.map((s, i) => {
          const color = COLORS[s.normalized] ?? '#6b7280'
          return (
            <circle
              key={s.platform.name}
              cx={cx}
              cy={cy}
              r={radius}
              fill="none"
              stroke={color}
              strokeWidth={strokeWidth}
              strokeDasharray={`${s.dash} ${s.gap}`}
              strokeDashoffset={-offsets[i]}
              strokeLinecap="round"
              transform={`rotate(-90 ${cx} ${cy})`}
            />
          )
        })}
        <text
          x={cx}
          y={cy - 4}
          textAnchor="middle"
          className="fill-foreground"
          fontSize="18"
          fontFamily={CHART_FONT}
          fontWeight="700"
        >
          {total > 999 ? `${(total / 1000).toFixed(1)}K` : total}
        </text>
        <text
          x={cx}
          y={cy + 12}
          textAnchor="middle"
          className="fill-muted-foreground"
          fontSize="9"
          fontFamily={CHART_FONT}
          fontWeight="500"
        >
          {t('analytics.total', { defaultValue: 'Total' })}
        </text>
      </svg>

      <div className="flex flex-col gap-2">
        {platforms.map((p) => {
          const normalized = normalizePlatform(p.name)
          const color = COLORS[normalized] ?? '#6b7280'
          const pct = total > 0 ? Math.round((p.count / total) * 100) : 0
          return (
            <div key={p.name} className="flex items-center gap-2 text-xs">
              <span
                className="h-2 w-2 shrink-0 rounded-full"
                style={{ backgroundColor: color }}
              />
              <span className="text-muted-foreground">
                {t(`platforms.${normalized}`)}
              </span>
              <span className="ml-auto font-semibold tabular-nums text-foreground">
                {pct}%
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

/* ── Sentiment Gauge ── */

function SentimentGauge({ sentiment }: { sentiment: SentimentBreakdown }) {
  const { t } = useTranslation()
  const { theme } = useTheme()
  const readSentimentColors = () => {
    const style = getComputedStyle(document.documentElement)
    return {
      positive: style.getPropertyValue('--sentiment-positive').trim(),
      neutral: style.getPropertyValue('--sentiment-neutral').trim(),
      negative: style.getPropertyValue('--sentiment-negative').trim(),
    }
  }
  const [sentimentColors, setSentimentColors] = useState(readSentimentColors)
  useEffect(() => {
    requestAnimationFrame(() => setSentimentColors(readSentimentColors()))
  }, [theme])
  const bars = [
    {
      label: t('posts.ai.sentiment.positive', { defaultValue: 'Positive' }),
      value: sentiment.positive,
      color: sentimentColors.positive,
    },
    {
      label: t('posts.ai.sentiment.neutral', { defaultValue: 'Neutral' }),
      value: sentiment.neutral,
      color: sentimentColors.neutral,
    },
    {
      label: t('posts.ai.sentiment.negative', { defaultValue: 'Negative' }),
      value: sentiment.negative,
      color: sentimentColors.negative,
    },
  ]

  return (
    <div className="flex flex-col gap-3">
      <div
        className="flex h-3 overflow-hidden rounded-full"
        role="img"
        aria-label={t('analytics.sentimentGauge.aria', {
          defaultValue:
            'Sentiment: {{positive}}% positive, {{neutral}}% neutral, {{negative}}% negative',
          positive: sentiment.positive,
          neutral: sentiment.neutral,
          negative: sentiment.negative,
        })}
      >
        {bars.map((b) => (
          <div
            key={b.label}
            aria-hidden="true"
            className="transition-[width] duration-500"
            style={{ width: `${b.value}%`, backgroundColor: b.color }}
          />
        ))}
      </div>

      <div className="flex flex-col gap-2">
        {bars.map((b) => (
          <div key={b.label} className="flex items-center justify-between text-xs">
            <div className="flex items-center gap-2">
              <span
                className="h-2 w-2 shrink-0 rounded-full"
                style={{ backgroundColor: b.color }}
              />
              <span className="text-muted-foreground">{b.label}</span>
            </div>
            <span className="font-semibold tabular-nums text-foreground">{b.value}%</span>
          </div>
        ))}
      </div>
    </div>
  )
}

/* ── Skeletons ── */

function PageSkeleton() {
  return (
    <div className="flex h-full flex-col gap-4">
      <div className="flex min-h-0 flex-1 flex-col gap-4 md:flex-row">
        <div className={cn(card, 'min-h-[420px] flex-1 md:min-h-0')}>
          <Skeleton className="h-full w-full rounded-lg" />
        </div>
        <div className="flex w-full shrink-0 flex-col gap-3 md:w-64 lg:w-72 xl:w-80">
          {[...Array(4)].map((_, i) => (
            <div key={i} className={card}>
              <div className="mb-2 flex justify-between">
                <Skeleton className="h-4 w-4" />
                <Skeleton className="h-3 w-10" />
              </div>
              <Skeleton className="mb-1 h-7 w-16" />
              <Skeleton className="h-3 w-24" />
            </div>
          ))}
          <div className={card}>
            <Skeleton className="mb-4 h-4 w-20" />
            <Skeleton className="mb-2 h-3 w-full rounded-full" />
            <Skeleton className="mb-2 h-3 w-full rounded-full" />
            <Skeleton className="h-3 w-full rounded-full" />
          </div>
        </div>
      </div>
      <div className={card}>
        <Skeleton className="mb-3 h-4 w-20" />
        <Skeleton className="h-[140px] w-full rounded-lg" />
      </div>
    </div>
  )
}

function CompactSkeleton() {
  return (
    <div className="flex flex-col gap-4 sm:gap-5">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className={card}>
            <div className="mb-2 flex justify-between">
              <Skeleton className="h-4 w-4" />
              <Skeleton className="h-3 w-10" />
            </div>
            <Skeleton className="mb-1 h-7 w-16" />
            <Skeleton className="h-3 w-24" />
          </div>
        ))}
      </div>
      <div className={card}>
        <Skeleton className="mb-3 h-4 w-20" />
        <Skeleton className="h-[140px] w-full rounded-lg" />
      </div>
      <div className="grid gap-3 sm:grid-cols-2 sm:gap-4">
        <div className={card}>
          <Skeleton className="mb-4 h-4 w-16" />
          <Skeleton className="mx-auto h-[120px] w-[120px] rounded-full" />
        </div>
        <div className={card}>
          <Skeleton className="mb-4 h-4 w-20" />
          <Skeleton className="mb-2 h-3 w-full rounded-full" />
          <Skeleton className="mb-2 h-3 w-full rounded-full" />
          <Skeleton className="h-3 w-full rounded-full" />
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 sm:gap-4">
        <div className={card}>
          <Skeleton className="mb-3 h-4 w-24" />
          <div className="flex flex-wrap gap-2 py-2">
            {[80, 56, 48, 40, 64, 32, 52, 36].map((w, i) => (
              <Skeleton key={i} className="h-5 rounded" style={{ width: w }} />
            ))}
          </div>
        </div>
        <div className={card}>
          <Skeleton className="mb-3 h-4 w-20" />
          <Skeleton className="mx-auto h-[200px] w-[200px] rounded-full" />
        </div>
      </div>
    </div>
  )
}
